import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { freightAPI } from '../api';
import {
  DocumentTextIcon,
  ChartBarIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { useLoading } from '../contexts/LoadingContext';  
import { TFSLoader, ErrorMessage, EmptyState } from '../components/ui';
import { DashboardRequestCard } from '../components/features';

const Dashboard = () => {
  const navigate = useNavigate();
  const { loading, setLoading } = useLoading();
  const [recentRequests, setRecentRequests] = useState([]);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const fetchDashboardData = useCallback(async (retryAttempt = 0) => {
    try {
      setLoading(true);
      setError(null);
      const requests = await freightAPI.getTop5Requests();
      setRecentRequests(requests);
      setRetryCount(0); // Reset retry count on success
    } catch (error) {
      console.error('Dashboard fetch error:', error);
      
      if (retryAttempt < 2) {
        // Retry up to 2 times with exponential backoff
        const delay = Math.pow(2, retryAttempt) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          setRetryCount(retryAttempt + 1);
          fetchDashboardData(retryAttempt + 1);
        }, delay);
        return;
      }
      
      setError(error.message || 'Failed to load recent requests. Please try again.');
      } finally {
      setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Status functions moved to common components

  const handleRequestClick = (requestId) => {
    navigate(`/requests/${requestId}`);
  };

  if (loading) {
    return <TFSLoader />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
      {/* Error Message */}
      <ErrorMessage
        error={error}
        onRetry={() => fetchDashboardData()}
        retryCount={retryCount}
        isRetrying={loading}
      />

      {/* Welcome Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Welcome to TFS Dashboard</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            Monitor and manage your international transport requests
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Recent Requests</h2>
                <ChartBarIcon className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="p-3 sm:p-6">
              {recentRequests.length === 0 ? (
                <EmptyState
                  icon={DocumentTextIcon}
                  title="No requests yet"
                  description="Create your first freight request to get started."
                />
              ) : (
                <div className="space-y-2">
                  {recentRequests.map((request) => (
                    <DashboardRequestCard
                      key={request.id}
                      request={request}
                      onClick={handleRequestClick}
                      variant="default"
                    />
                  ))}
                  <div className="text-center pt-2">
                    <button 
                      onClick={() => navigate('/requests')}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium inline-flex items-center"
                    >
                      View all requests
                      <ArrowRightIcon className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">Quick Actions</h2>
            </div>
            <div className="p-3 sm:p-4">
              <div className="space-y-2">                
                <button 
                  onClick={() => navigate('/requests')}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-indigo-100 rounded-md mr-3">
                      <DocumentTextIcon className="h-5 w-5 text-indigo-600" />
                    </div>
                    <span className="font-medium text-gray-800">View All Requests</span>
                  </div>
                  <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                </button>
                
                <button 
                  onClick={() => navigate('/analytics')}
                  className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 transition-colors text-left"
                >
                  <div className="flex items-center">
                    <div className="p-2 bg-emerald-100 rounded-md mr-3">
                      <ChartBarIcon className="h-5 w-5 text-emerald-600" />
                    </div>
                    <span className="font-medium text-gray-800">Analytics Dashboard</span>
                  </div>
                  <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;